import React from 'react'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import { FeatureFlagValue, FeatureFlags } from '@mobi/settings/featureFlags'
import { useAppSelector } from '@core/Store/hooks'
import { ExpandableToolboxSection } from './Common/ExpandableToolboxSection'

export const FeatureToggles = () => {
  const [isActiveFlagsOnly, setIsActiveFlagsOnly] = React.useState(true)

  const ldFeatures = useAppSelector(state => state.features.features)

  const sortedLdFeatures = React.useMemo(() => {
    const keysInCode = Object.values(FeatureFlags).map(flag => flag.key as string)
    const flags = Object.entries(ldFeatures).sort((a, b) =>
      a[0].toUpperCase().localeCompare(b[0].toUpperCase())
    )
    const filteredFlags = isActiveFlagsOnly
      ? flags.filter(([key]) => keysInCode.includes(key))
      : flags
    return filteredFlags
  }, [ldFeatures, isActiveFlagsOnly])

  return (
    <List title='Feature Flags' itemSize='dynamic' variant='card'>
      <ListItem
        text='Display Active Flags Only'
        subText='Filter LD flags to only include keys within code'
        sizeOverride='medium'
        contentRight={
          <Toggle
            id='active-flags'
            onToggle={() => setIsActiveFlagsOnly(curr => !curr)}
            isOn={isActiveFlagsOnly}
          />
        }
      />
      <ListItem
        content={
          <div style={{ paddingBlock: '0.5rem' }}>
            <ExpandableToolboxSection
              icon='SolidList'
              shouldUseTheme={false}
              heading={`${isActiveFlagsOnly ? 'Active' : 'All'} LaunchDarkly Flags`}
            >
              <List shouldUseInlinePadding={false} bottomBorder='none' itemSize='dynamic'>
                {sortedLdFeatures.map(([key, value]) =>
                  typeof value === 'object' ? (
                    <ListItem
                      key={key}
                      content={
                        <Flex flexDirection='column' flex='1'>
                          <span>{key}</span>
                          <span>{format(value)}</span>
                        </Flex>
                      }
                    />
                  ) : (
                    <ListItem key={key} text={key} contentRight={format(value)} />
                  )
                )}
              </List>
            </ExpandableToolboxSection>
          </div>
        }
      />
    </List>
  )
}

const format = (flagValue: FeatureFlagValue) => {
  if (typeof flagValue === 'undefined') {
    return 'Undefined'
  } else if (typeof flagValue === 'boolean') {
    return (
      <StatusBadge color={flagValue ? 'green' : 'red'}>
        <Icon size='1.4rem' name={flagValue ? 'LineCheck' : 'SolidXClose'} /> {flagValue.toString()}
      </StatusBadge>
    )
  } else if (typeof flagValue === 'string') {
    return flagValue
  } else if (typeof flagValue === 'object') {
    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          wordBreak: 'keep-all',
        }}
      >
        {JSON.stringify(flagValue, null, 2)}
      </pre>
    )
  } else if (typeof flagValue === 'number') {
    return flagValue.toString()
  } else {
    return 'Unrecognised type'
  }
}
